import gql from "graphql-tag";
import { useEffect, useRef, useState } from "react";
import { InducteeHeroFragment } from "@graphqlTypes";
import { Image, ImageAttribution, InducteeHeroContent } from "@components";
import { motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

import styles from "./InducteeHero.module.scss";
import className from "classnames/bind";
import { getStringParam } from "@/utils/urlParams";
const cx = className.bind(styles);

type InducteeHeroProps = {
  data: InducteeHeroFragment['inducteeData']['inducteeHero'];
  inducteeInfo: {
    title: string;
    inductedBy?: string;
    category: string;
    class: string;
  }
};

// Renders the hero section for inductees
export default function InducteeHero({ data, inducteeInfo }: InducteeHeroProps) {
  // Destructure data and inductee info
  const { image, mobileImage, headline, showReverb, imageAttribution } = data;
  const imageWrapper = useRef<HTMLDivElement>(null);
  const desktopImageRef = useRef<HTMLImageElement>(null);
  const mobileImageRef = useRef<HTMLImageElement>(null);
  const [canAnimateIn, setCanAnimateIn] = useState(false);

  const onImageLoad = () => {
    // Check if the relevant image for the current screen size is loaded
    // This will be called multiple times if both images load, but that's fine
    setCanAnimateIn(true);
  };

  // -------------------- Parallax

  const scaleEnd = parseFloat(getStringParam('inducteeHeroScaleEnd', '0.95'));
  const transformVal = parseFloat(getStringParam('inducteeHeroTransformEnd', '0.5'));
  const transformEnd = transformVal * 100;
  const brightnessSpeed = parseFloat(getStringParam('inducteeHeroBrightnessSpeed', '0.8'));

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger, useGSAP);

    if (!imageWrapper.current) return;

    // Create animation
    const animateImage = (img: HTMLImageElement) => {
      gsap.fromTo(img,
        {
          scale: 1.08,
          y: "0%",
          filter: "brightness(1)"
        },
        {
          scale: scaleEnd,
          y: `${transformEnd}%`,
          filter: `brightness(0)`,
          ease: "none",
          scrollTrigger: {
            trigger: imageWrapper.current,
            start: "top top",
            end: "bottom top",
            scrub: true,
            invalidateOnRefresh: true,
          }
        }
      );
    };

    // Use matchMedia to animate the correct image based on screen size
    const mm = gsap.matchMedia();

    // Mobile: animate mobile image
    mm.add("(max-width: 767px)", () => {
      if (mobileImageRef.current) {
        animateImage(mobileImageRef.current);
      }
    });

    // Desktop: animate desktop image
    mm.add("(min-width: 768px)", () => {
      if (desktopImageRef.current) {
        animateImage(desktopImageRef.current);
      }
    });

    return () => mm.revert();
  }, { scope: imageWrapper, dependencies: [scaleEnd, transformEnd, brightnessSpeed] });

  useEffect(() => {
    // Check if images are already loaded (e.g., from cache)
    const desktopLoaded = desktopImageRef.current?.complete;
    const mobileLoaded = mobileImageRef.current?.complete;

    // If either image is loaded (depending on which is visible), allow animation
    if (desktopLoaded || mobileLoaded) {
      setCanAnimateIn(true);
    }
  }, []);

  return (
    <motion.section
      className={cx(["inductee-hero"])}
      initial={{ opacity: 0 }}
      animate={canAnimateIn ? { opacity: 1 } : {}}
      transition={{ duration: AnimVals.lg.duration, delay: 0.1, ease: AnimVals.ease }}
    >
      {/* Inductee image */}
      <div className={cx("inductee-hero__image-wrapper")} ref={imageWrapper}>
        <Image
          className={cx(["image", { 'image--desktop': !!mobileImage?.node }])}
          image={image}
          onLoad={onImageLoad}
          ref={desktopImageRef}
        />
        {mobileImage?.node && (
          <Image
            className={cx(["image", "image--mobile"])}
            image={mobileImage}
            onLoad={onImageLoad}
            ref={mobileImageRef}
          />
        )}
        {imageAttribution && <ImageAttribution text={imageAttribution} className={cx("hero-image-attribution")} />}
      </div>
      <InducteeHeroContent
        headline={headline}
        inducteeInfo={inducteeInfo}
        showReverb={showReverb}
        hasReveal={true}
        canAnimateIn={canAnimateIn}
      />
    </motion.section>
  );
}

// GraphQL fragment for fetching inductee hero data
InducteeHero.fragments = {
  entry: gql`
    fragment InducteeHero on Inductee {
      inducteeData {
        inducteeHero {
          showReverb
          imageAttribution
          headline {
            line
          }
          image {
            ...ACFMedia
          }
          mobileImage {
            ...ACFMedia
          }
        }
      }
    }
  `,
};
